import React, { useEffect, useMemo } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    Grid,
    makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ITheme } from '../../themes/custom/ITheme';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import ColorList from './ColorList';
import * as actions from '../../store/actions';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import SnackBarType from '../../objects/app/snackbar';
import clsx from 'clsx';
import { colorsStrToHex } from './Colors';
import { Skeleton } from '@material-ui/lab';
import transparentImg from '../../img/transparent.png';

const useStyles = makeStyles((theme: ITheme) => {
    return {
        dialogContent: {},
        image: {
            width: '100%',
            maxWidth: '800px',
            maxHeight: '800px',
            padding: '10px',
        },
        noPadding: {
            padding: 0,
        },
        noOverflow: {
            overflow: 'hidden',
        },
        overwrite: {
            textAlign: 'center',
        },
        imgWrap: {
            backgroundImage: `url(${transparentImg})`,
        },
        colorList: {
            padding: '5px',
        },
        overwriteLabel: {
            '&MuiTypography-root': {
                color: theme.palette.text.secondary,
            },
            '&Mui-disabled': {
                color: theme.palette.text.secondary,
            },
        },
        overwriteCheckbox: {
            border: '1px solid red',
            '&MuiCheckbox-root': {
                '&MuiSvgIcon-root': {
                    color: theme.palette.text.secondary,
                },
            },
            '&MuiSvgIcon-root': {
                color: theme.palette.text.secondary,
            },
        },
    };
});

interface Props {
    onSubmit: (svg: string, overwrite: boolean) => void;
}

const ChangeGraphicColorDialog: React.FC<Props> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { onSubmit } = props;
    const dispatch = useDispatch();
    const [svg, setSvg] = React.useState<string | null>(null);
    const [overwrite, setOverwrite] = React.useState<boolean>(true);
    const [colors, setColors] = React.useState<string[]>([]);
    const { data: graphicData, loading: graphicDataLoading } = useTypedSelector(
        (state) => state.changeGraphicColor.loadGraphicSvg
    );

    const { loading: saveSvgLoading, success: saveSvgSuccess } =
        useTypedSelector((state) => state.changeGraphicColor.saveGraphicSvg);

    const svgData = useMemo<string | null>(() => {
        if (svg) {
            const buff = new Buffer(svg);
            return buff.toString('base64');
        }
        return null;
    }, [svg]);

    React.useEffect(() => {
        if (graphicData && graphicData.svg) {
            let newSvg = graphicData.svg;
            const regularForPath = /<path.*?\/>/g;
            const regularForPathWithFill = /<path.*?fill="(.*?)".*?\/>/g;
            const allPaths = newSvg.match(regularForPath);
            const allPathsWithFill = newSvg.match(regularForPathWithFill);

            // difference between allPaths and allPathsWithFill
            if (allPaths && allPathsWithFill) {
                const difference = allPaths.filter(
                    (x) => !allPathsWithFill.includes(x)
                );
                // add fill color black to all paths without fill
                if (difference) {
                    difference.forEach((path) => {
                        newSvg = newSvg.replace(
                            path,
                            path.replace('<path ', '<path fill="#000000" ')
                        );
                    });
                }
            }

            // replace text representations of colors with hex
            [
                {
                    regular: /fill="([a-zA-Z]*?)"/g,
                    leftReplace: 'fill="',
                    rightReplace: '"',
                },
                {
                    regular: /fill:([a-zA-Z]*?);/g,
                    leftReplace: 'fill:',
                    rightReplace: ';',
                },
            ].forEach((colorReg) => {
                const allColors = newSvg.match(colorReg.regular);
                if (allColors) {
                    allColors.forEach((color) => {
                        // get just color name
                        const colorName = color
                            .replace(colorReg.leftReplace, '')
                            .replace(colorReg.rightReplace, '');
                        let newColor = colorsStrToHex
                            .filter(
                                (colorObj) =>
                                    colorObj.str === colorName.toLowerCase()
                            )
                            .shift();
                        if (newColor) {
                            newSvg = newSvg.replaceAll(
                                color,
                                color.replace(
                                    color,
                                    colorReg.leftReplace +
                                        newColor.hex +
                                        colorReg.rightReplace
                                )
                            );
                        }
                    });
                }
            });

            setSvg(newSvg);

            const newColors: any[] = [];
            let result;
            if (newSvg) {
                result = newSvg.match(
                    /[rgbRGB]{3}\( *([0-9]+), *([0-9]+), *([0-9]+) *\)/g
                );
                if (result != null) {
                    newColors.push(...result);
                }
                result = newSvg.match(/#([0-9,a-f,A-F]{3})[^0-9,^a-f,^A-F]/g);
                if (result != null) {
                    newColors.push(
                        ...result.map((color) => {
                            return color.substring(0, 4);
                        })
                    );
                }
                result = newSvg.match(/#[0-9,a-f,A-F]{6}/g);
                if (result != null) {
                    newColors.push(...result);
                }
            }
            setColors(
                newColors.filter(
                    (color, index) => newColors.indexOf(color) === index
                )
            );
        }
    }, [graphicData]);

    const handleOnColorChange = (oldColor: string, newColor: string) => {
        let newSvg = svg;
        if (newSvg) {
            newSvg = newSvg.replaceAll(oldColor, newColor);
            setSvg(newSvg);
        }

        // replace color in colors array
        const newColors = colors.map((color) => {
            if (color === oldColor) {
                return newColor;
            }
            return color;
        });

        setColors(newColors);
    };

    useEffect(() => {
        if (saveSvgSuccess) {
            setSvg(null);
            setOverwrite(true);
            setColors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveSvgSuccess]);

    const handleOnSubmit = () => {
        if (svg) {
            onSubmit(svg, overwrite);
        }
    };

    const handleOnCancel = () => {
        dispatch(actions.loadGraphicSvgReset());
        setSvg(null);
    };

    const currentUsedGraphics: number[] =
        window.getGraphicIdsFromCurrentDesign();

    return (
        <>
            <DialogContent
                className={clsx(
                    classes.noPadding,
                    classes.noOverflow,
                    classes.dialogContent
                )}
            >
                <Grid
                    container
                    alignItems="center"
                    spacing={1}
                    justifyContent="center"
                >
                    <Grid
                        item
                        sm={10}
                        xs={12}
                        className={clsx(classes.noPadding, classes.imgWrap)}
                    >
                        {graphicDataLoading && (
                            <Skeleton variant="rect" width={500} height={500} />
                        )}
                        {!graphicDataLoading && svgData && (
                            <img
                                alt=""
                                className={classes.image}
                                height="500px"
                                src={`data:image/svg+xml;base64,${svgData}`}
                            />
                        )}
                    </Grid>
                    <Grid item sm={2} xs={12} className={classes.colorList}>
                        <ColorList
                            onChange={handleOnColorChange}
                            colors={colors}
                            loading={graphicDataLoading}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Grid container>
                    <Grid item xs={12}>
                        <Grid container direction="row-reverse" spacing={2}>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={handleOnSubmit}
                                >
                                    {saveSvgLoading && (
                                        <CircularProgress size={20} />
                                    )}
                                    {!saveSvgLoading && <>{t('Ok')}</>}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleOnCancel}
                                >
                                    {t('Cancel')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <FormGroup>
                                    <FormControlLabel
                                        className={classes.overwriteLabel}
                                        control={
                                            <Checkbox
                                                checked={overwrite}
                                                className={
                                                    classes.overwriteCheckbox
                                                }
                                                onClick={() => {
                                                    if (
                                                        graphicData &&
                                                        currentUsedGraphics.includes(
                                                            graphicData.idGraphic
                                                        ) &&
                                                        !overwrite
                                                    ) {
                                                        dispatch(
                                                            actions.addSnackbar(
                                                                t(
                                                                    'This graphic is used in current design and will be overwritten everywhere.'
                                                                ),
                                                                SnackBarType.WARNING,
                                                                false
                                                            )
                                                        );
                                                    }
                                                    setOverwrite(!overwrite);
                                                }}
                                            />
                                        }
                                        label={t(
                                            'Overwrite this graphic'
                                        ).toString()}
                                    />
                                </FormGroup>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
        </>
    );
};

export default ChangeGraphicColorDialog;
